/**
 * Easy Read version of the BTR Health Service Agreement.
 *
 * NDIS Easy Read style: plain English, short sentences, one idea per line,
 * emoji icons per section, no legal jargon. Content is built from the same
 * participant data dict as the standard agreement.
 */

export function buildEasyReadServiceAgreement({ participant, servicesSummary, startDate, endDate } = {}) {
  const name = participant?.name || participant?.participant_name || "you";
  const ndis = participant?.ndis_number || "your NDIS number";
  const rawPlan = participant?.ndis_plan_type || "";
  const planText = /self/i.test(rawPlan)
    ? "you manage it yourself"
    : /plan/i.test(rawPlan)
      ? "a Plan Manager pays the bills for you"
      : /ndia|agency/i.test(rawPlan)
        ? "the NDIS pays us directly"
        : /private/i.test(rawPlan)
          ? "you pay privately"
          : "we will check with you";
  const address = participant?.address || "your address";
  const emergency = participant?.emergency_contact_name;
  const start = startDate || "the day you sign this";
  const end = endDate || "12 months after the start date";

  return [
    {
      icon: "👋",
      title: "Hello!",
      lines: [
        `This is an agreement between BTR Health and ${name}.`,
        "It tells you what we will do for you.",
        "And it tells you what we ask you to do.",
      ],
    },
    {
      icon: "👤",
      title: "About you",
      lines: [
        `Your name: ${name}.`,
        `Your NDIS number: ${ndis}.`,
        `Your address: ${address}.`,
        `How your plan is managed: ${planText}.`,
        emergency ? `Your emergency contact: ${emergency}.` : "You have not given us an emergency contact yet.",
      ],
    },
    {
      icon: "🤝",
      title: "What BTR Health will do",
      lines: [
        "We will send you a support worker.",
        "We will help you with the services you choose.",
        servicesSummary
          ? `The services we agreed on are: ${servicesSummary}.`
          : "You and BTR Health will agree on the services in each booking.",
        "We will always treat you with respect.",
        "We will keep your information safe and private.",
      ],
    },
    {
      icon: "🙋",
      title: "What you will do",
      lines: [
        "Tell us if you cannot make it to a shift.",
        "Try to tell us at least 2 days before.",
        "Be safe and respectful to your worker.",
        "Tell us if something is wrong.",
      ],
    },
    {
      icon: "💰",
      title: "About money",
      lines: [
        "The price of every shift comes from the NDIS Price Guide.",
        "Weekdays cost one price. Weekends cost more. Public holidays cost more.",
        "Your plan pays for our services. You do NOT pay from your own money.",
        "You do NOT pay a fee to BTR Health for using our website.",
      ],
    },
    {
      icon: "🙅",
      title: "Cancelling a shift",
      lines: [
        "You can cancel any shift.",
        "If you cancel more than 7 days before the shift — no charge.",
        "If you cancel 2 to 7 days before — no charge.",
        "If you cancel less than 2 days before — your plan is charged the full price of the shift.",
        "This is the NDIS short-notice cancellation rule.",
      ],
    },
    {
      icon: "🛑",
      title: "Ending this agreement",
      lines: [
        "You can end this agreement at any time.",
        "Tell us in writing (email is fine).",
        "We can also end this agreement if we cannot keep you safe.",
        "We will give you 14 days notice if we end it.",
      ],
    },
    {
      icon: "📅",
      title: "How long this agreement lasts",
      lines: [
        `It starts on: ${start}.`,
        `It ends on: ${end}.`,
        "We can make a new one when this one ends.",
      ],
    },
    {
      icon: "🤔",
      title: "If something goes wrong",
      lines: [
        "Tell your worker. Tell BTR Health.",
        "Email: hello@btrhealth.com.au — Phone: (03) 9961 6066.",
        "You can also complain to the NDIS Quality and Safeguards Commission on 1800 035 544.",
        "We will listen and try to fix it.",
      ],
    },
    {
      icon: "✍️",
      title: "Signing this agreement",
      lines: [
        "When you sign below you are saying:",
        "  ✓ I have read this agreement.",
        "  ✓ I agree to it.",
        "  ✓ I know I can ask questions any time.",
      ],
    },
  ];
}
